import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import {
  DuplicateProjectDocument,
  type DuplicateProjectMutation,
  type DuplicateProjectMutationVariables,
} from "@src/__generated__/graphql";
import type { Task } from "@src/__generated__/urql-graphql";
import { CancelButton } from "@src/components/ui-kit/Button/CancelButton";
import { Form } from "@src/components/ui-kit/Form";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { useStore } from "@src/utils/hooks";
import { FieldState } from "formstate";
import { observer, useLocalObservable } from "mobx-react-lite";
import type { ChangeEvent, FC } from "react";

type DuplicateProjectModalProps = {
  modalId?: string;
  projectId: Task["id"];
  /**
   * workaround, once planning will be merged we can pass it through dedicated store
   */
  onSubmit?: (project: DuplicateProjectMutation["duplicateProject"]) => void;
};

export const DuplicateProjectModal: FC<DuplicateProjectModalProps> = observer(
  function DuplicateProjectModal({ modalId, projectId, onSubmit }) {
    const { UIStore } = useStore();
    const form = useLocalObservable(() => ({
      with_tasks: new FieldState(false),
      with_attachments: new FieldState(false),
      handleDuplicateTasksChange({ target }: ChangeEvent<HTMLInputElement>) {
        this.with_tasks.onChange(target.checked);
        if (target.checked) return;
        this.with_attachments.reset();
      },
      duplicateMutator: new MutationHelper<
        DuplicateProjectMutation,
        DuplicateProjectMutationVariables
      >(DuplicateProjectDocument),
    }));

    const _onSubmit = async () => {
      const [data] = await form.duplicateMutator.mutate({
        id: projectId,
        with_tasks: form.with_tasks.$,
        with_task_attachments: form.with_attachments.$,
      });

      if (!data?.duplicateProject) return;
      onClose();
      onSubmit?.(data.duplicateProject);
    };

    const onClose = () => {
      if (!modalId) return;
      UIStore.dialogs.closeModal(modalId);
    };

    return (
      <Modal isOpen onClose={onClose} trapFocus={false}>
        <ModalOverlay />
        <ModalContent>
          <Form onSubmit={_onSubmit}>
            <ModalHeader>
              <Trans>Duplicate project</Trans>
            </ModalHeader>

            <ModalCloseButton />

            <ModalBody>
              <Text mb="2" fontWeight="semibold">
                <Trans>Would you like to copy:</Trans>
              </Text>
              <Box mb="1">
                <Checkbox
                  isChecked={form.with_tasks.value}
                  onChange={form.handleDuplicateTasksChange}
                >
                  <Trans>All tasks from previous project</Trans>
                </Checkbox>
              </Box>
              <Box>
                <Checkbox
                  isChecked={form.with_attachments.value}
                  isDisabled={!form.with_tasks.value}
                  onChange={({ target }) => {
                    form.with_attachments.onChange(target.checked);
                  }}
                >
                  <Trans>Attachments from tasks</Trans>
                </Checkbox>
              </Box>
            </ModalBody>

            <ModalFooter>
              <VStack align="end">
                <ButtonGroup>
                  <CancelButton onClick={onClose} />
                  <Button
                    isLoading={form.duplicateMutator.isLoading.value}
                    type="submit"
                  >
                    <Trans>Duplicate</Trans>
                  </Button>
                </ButtonGroup>
                {form.duplicateMutator.isLoading.value && (
                  <Text fontSize="2xs">
                    <Trans>
                      Please note that this action may take approximately 30
                      seconds to complete.
                    </Trans>
                  </Text>
                )}
              </VStack>
            </ModalFooter>
          </Form>
        </ModalContent>
      </Modal>
    );
  },
);
